import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ProductApiService } from '../../../services/product-api.service';
import { ProductList } from '../../../../../shared/models/product-list';
import { GetAllProductsResponseBody } from '../../../models/get-all-products-response-body';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType } from '../../../../../notifications/models/toast-notification-type';
import { RouterLink } from '@angular/router';
import { FilteringChipsComponent } from "../../../../../shared/components/filtering-chips/filtering-chips.component";
import { TablePaginationComponent } from "../../../../../shared/components/table-pagination/table-pagination.component";
import { TableSkeletonRowComponent } from "../../../../../shared/components/table-skeleton-row/table-skeleton-row.component";
import { LimitOffsetPagination } from '../../../../../shared/models/limit-offset-pagination';
import { debounceTime, distinctUntilChanged, Subject, switchMap } from 'rxjs';
import { FiltersDropdownComponent, FilterDef, FilterValues } from '../../../../../shared/filters-dropdown';
import { ExportExcelButtonComponent } from '../../../../../shared/components/export-excel-button/export-excel-button.component';
import { Category } from '../../../../../shared/models/category';
import { Brand } from '../../../../../shared/models/brand';
import { Occasion } from '../../../../../shared/models/occasion';
import { forkJoin } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../../shared/services/translations/language.service';

@Component({
  selector: 'app-products-page',
  imports: [CommonModule, FormsModule, RouterLink, FilteringChipsComponent, TablePaginationComponent, TableSkeletonRowComponent, FiltersDropdownComponent, ExportExcelButtonComponent, TranslateModule],
  templateUrl: './products-page.component.html',
  styleUrl: './products-page.component.css'
})
export class ProductsPageComponent implements OnInit {


  apiService = inject(ProductApiService);
  productsResponse: GetAllProductsResponseBody | null = null;
  toastNotificationService = inject(ToastNotificationService);
  translate = inject(TranslateService);
  languageService = inject(LanguageService);

  pagination: LimitOffsetPagination = { limit: 10, offset: 0 };
  search = '';
  searchSubject = new Subject<string>(); // for debounce

  statusFilters: { name: string, value: string }[] = [];

  selectedFilter: string = '';

  categories: Category[] = [];
  brands: Brand[] = [];
  occasions: Occasion[] = [];
  filterValues: FilterValues = {};
  productFilters: FilterDef[] = [];

  ngOnInit(): void {
    this.statusFilters = [
      { name: 'products.status_filters.all', value: '' },
      { name: 'products.status_filters.active', value: 'active' },
      { name: 'products.status_filters.inactive', value: 'inactive' },
      { name: 'products.status_filters.deleted', value: 'deleted' }
    ];
    this.getProducts();
    this.loadFilterSources();

    // setup debounced search
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
    ).subscribe(searchText => {
      this.search = searchText;
      this.pagination = { limit: 10, offset: 0 };
      this.getProducts();
    });

    this.translate.onLangChange.subscribe(() => {
      this.loadFilterSources();
      this.getProducts();
    });
  }

  private loadFilterSources(): void {
    forkJoin({
      categories: this.apiService.getCategories(),
      brands: this.apiService.getBrands(),
      occasions: this.apiService.getOccasions(),
    }).subscribe({
      next: ({ categories, brands, occasions }) => {
        this.categories = categories;
        this.brands = brands.results;
        this.occasions = occasions;
        this.productFilters = this.buildFilters();
      },
      error: () => {
        this.productFilters = this.buildFilters();
      },
    });
  }

  private buildFilters(): FilterDef[] {
    const currency = this.translate.instant('common.currency');
    return [
      {
        kind: 'numberRange',
        key: 'price',
        label: `${this.translate.instant('products.filters.price_range')} (${currency})`,
        minPlaceholder: this.translate.instant('products.filters.from'),
        maxPlaceholder: this.translate.instant('products.filters.to'),
      },
      {
        kind: 'select',
        key: 'category',
        label: this.translate.instant('products.filters.category'),
        placeholder: this.translate.instant('products.filters.all_categories'),
        options: this.categories.map(c => ({ label: c.name, value: c.id })),
      },
      {
        kind: 'dependentSelect',
        key: 'subcategory',
        label: this.translate.instant('products.filters.subcategory'),
        placeholder: this.translate.instant('products.filters.all_subcategories'),
        dependsOn: 'category',
        getOptions: (values) => {
          const cat = this.categories.find(c => c.id === values['category']);
          return cat ? cat.subcategories.map(s => ({ label: s.name, value: s.id })) : [];
        },
      },
      {
        kind: 'select',
        key: 'brand',
        label: this.translate.instant('products.filters.brand'),
        placeholder: this.translate.instant('products.filters.all_brands'),
        options: this.brands.map(b => ({ label: b.name, value: b.id })),
      },
      {
        kind: 'select',
        key: 'occasion',
        label: this.translate.instant('products.filters.occasion'),
        placeholder: this.translate.instant('products.filters.all_occasions'),
        options: this.occasions.map(o => ({ label: o.name, value: o.id })),
      },
      {
        kind: 'rating',
        key: 'rating',
        label: this.translate.instant('products.filters.rating'),
      },
    ];
  }

  onFiltersChange(values: FilterValues): void {
    this.filterValues = values;
    this.pagination = { limit: 10, offset: 0 };
    this.getProducts();
  }

  onSearchChange(event: any) {
    this.searchSubject.next(event.target.value.trim());
  }

  getProducts() {
    this.productsResponse = null;

    this.apiService.getProducts(this.pagination, this.selectedFilter, this.search, this.toApiFilters()).subscribe({
      next: (response) => {
        this.productsResponse = response;
      },
      error: (err) => {
        this.toastNotificationService.createNotification(this.translate.instant('products.errors.load_failed_message'), ToastNotificationType.ERROR);
      }
    });
  }

  private toApiFilters(): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    const price = this.filterValues['price'];
    if (price?.min != null) out['min_price'] = price.min;
    if (price?.max != null) out['max_price'] = price.max;
    if (this.filterValues['category'] != null) out['category'] = this.filterValues['category'];
    if (this.filterValues['subcategory'] != null) out['subcategory__in'] = this.filterValues['subcategory'];
    if (this.filterValues['brand'] != null) out['brand'] = this.filterValues['brand'];
    if (this.filterValues['occasion'] != null) out['occasion'] = this.filterValues['occasion'];
    const rating = this.filterValues['rating'];
    if (rating != null) {
      out['min_rating'] = rating;
      out['max_rating'] = rating + 0.99;
    }
    return out;
  }

  onFilter(filter: string) {
    this.selectedFilter = filter;
    this.getProducts();
    this.pagination = { limit: 10, offset: 0 };
  }

  updatePagination(pagination: LimitOffsetPagination) {
    this.pagination = pagination;
    this.getProducts();
  }

  isExporting = false;

  onExportExcel(): void {
    if (this.isExporting) return;
    this.isExporting = true;
    this.apiService.exportProducts(this.selectedFilter, this.search, this.toApiFilters()).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `products-${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        this.isExporting = false;
      },
      error: () => {
        this.toastNotificationService.createNotification(
          this.translate.instant('products.errors.load_failed_message'),
          ToastNotificationType.ERROR,
        );
        this.isExporting = false;
      },
    });
  }

}
